import { catchError, map, of, tap, filter, take } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { LocationDTO } from '../../../../dto/location/location.dto';
import { UserDTO } from '../../../../dto/users/user.dto';
import { User } from '../../../../models/user/User.model';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { Router } from '@angular/router';
import { environment } from '../../../../environment/environment';
import { AuthService } from '../../../../services/auth/auth.service';
import { UserGroupSummaryItem } from '../../../../dto/user-group/user-group.dto';

export function locations_users(componentDataSourceService: ComponentDataSourceService) {
  /* ---------------------------------------------------------------------- */
  /*  Services                                                               */
  /* ---------------------------------------------------------------------- */
  const router       = componentDataSourceService.injector.get(Router);
  const authService  = componentDataSourceService.injector.get(AuthService);

  const ALLOWED_ROLES = ['orgadmin', 'superadmin', 'orgbruger', 'uddansvarlig'];

  /* ---------------------------------------------------------------------- */
  /*  Base-columns (always present)                                          */
  /* ---------------------------------------------------------------------- */
  const baseColumns: any[] = [
    { key: 'name', header: 'Navn', type: 'text', sortable: true },
    {
      key: 'roles',
      header: 'Roller',
      type: 'text',
      sortable: true,
      transform: (roles: any) => {
        if (!roles) return '';

        const excluded = ['bruger', 'superadmin', 'orguser', 'orgbruger', 'primaer'];
        if (Array.isArray(roles)) {
          return roles
            .filter(r => !excluded.includes(r))
            .map(translateRole)
            .join(', ');
        }
        if (typeof roles === 'object') {
          return Object.keys(roles)
            .filter(r => !excluded.includes(r))
            .map(translateRole)
            .join(', ');
        }
        return excluded.includes(roles) ? '' : translateRole(String(roles));
      }
    }
  ];

  const groupsColumn = {
    key: 'groups',
    header: 'Grupperinger',
    type: 'text',
    sortable: true,
    transform: (_v: unknown, row?: User) =>
      (row?.groups ?? []).map((g: UserGroupSummaryItem) => g.name).join(', ') || '—',
    wrap: true,
  };

  const tableFilters: any[] = [];
  const groupTableOptions: any[] = [];

  function collectAvailableGroups(meta: any, users: User[]): UserGroupSummaryItem[] {
    const fromMeta: UserGroupSummaryItem[] = meta?.available_groups ?? [];
    if (fromMeta.length > 0) {
      return fromMeta;
    }

    const byId = new Map<number, string>();
    for (const user of users) {
      for (const group of user.groups ?? []) {
        byId.set(group.group_id, group.name);
      }
    }
    return Array.from(byId.entries())
      .map(([group_id, name]) => ({ group_id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'da'));
  }

  function syncGroupUi(meta: any, users: User[]): void {
    const anyUserHasGroups = users.some(u => (u.groups?.length ?? 0) > 0);
    const showGroupColumn = Boolean(meta?.show_group_column) || anyUserHasGroups;
    const availableGroups = collectAvailableGroups(meta, users);

    const groupsIdx = columns.findIndex(c => c.key === 'groups');
    if (showGroupColumn && groupsIdx === -1) {
      const rolesIdx = columns.findIndex(c => c.key === 'roles');
      const insertAt = rolesIdx >= 0 ? rolesIdx + 1 : columns.length;
      columns.splice(insertAt, 0, groupsColumn);
    } else if (!showGroupColumn && groupsIdx >= 0) {
      columns.splice(groupsIdx, 1);
    }

    const groupOptionIdx = groupTableOptions.findIndex(o => o.id === 'group_id');
    const preservedValue = groupOptionIdx >= 0
      ? groupTableOptions[groupOptionIdx].default
      : null;

    if (showGroupColumn && availableGroups.length > 0) {
      const options = [
        { label: 'Alle', value: null },
        ...availableGroups.map(g => ({ label: g.name, value: g.group_id })),
      ];
      const optionDef = {
        id: 'group_id',
        label: 'Gruppering',
        type: 'select',
        default: preservedValue ?? null,
        options,
        onChange: () => {},
      };
      if (groupOptionIdx >= 0) {
        groupTableOptions[groupOptionIdx] = optionDef;
      } else {
        groupTableOptions.push(optionDef);
      }
    } else if (groupOptionIdx >= 0) {
      groupTableOptions.splice(groupOptionIdx, 1);
    }

    tableFilters.length = 0;
  }

  function buildTableSettings() {
    return {
      columns: [...columns],
      pageable: true,
      pageSize: 50,
      pageSizeOptions: [5, 10, 25, 50],
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'name',
      defaultSortDirection: 'asc',
      filters: [...tableFilters],
      ...(groupTableOptions.length > 0 ? { tableOptions: [...groupTableOptions] } : {}),
    };
  }

  /* ---------------------------------------------------------------------- */
  /*  Educational paths column (always visible, but with different styling) */
  /* ---------------------------------------------------------------------- */
  const educationalPathsColumn = {
    key: 'educational_paths',
    header: 'Uddannelsesveje',
    type: 'component',
    componentName: 'duna-educational-path-badge',
    componentInputs: (user: any) => {
      const educationalPaths = user.status?.educational_paths || [];
      
      return {
        educationalPaths: educationalPaths,
        currentYear: new Date().getFullYear(),
        user: user,
        showStatus: false // Will be overridden based on permissions
      };
    },
    sortable: false,
    alignment: 'center',
    width: '140px'
  };

  /* ---------------------------------------------------------------------- */
  /*  Sensitive columns (only for users with permissions)                   */
  /* ---------------------------------------------------------------------- */
  const sensitiveColumns: any[] = [
    { 
      key: 'validation_status', 
      header: 'DUNA Status', 
      type: 'component',
      componentName: 'duna-validation-badge',
      componentInputs: (user: any) => {
        // Try multiple ways to access validation_status, same as brugere_status
        const validationStatus = user.validation_status || 
                               user.status?.validation_status || 
                               'no_data';
        
        return {
          status: validationStatus,
          customTooltip: getValidationTooltip(validationStatus)
        };
      },
      sortable: true
    },
    {
      key: 'registered_percentage',
      header: 'Fremgang',
      type: 'component',
      componentName: 'duna-user-progress-label',
      componentInputs: (user: User) => ({
        registeredPercentage: user.status?.registered_percentage ?? 0,
        progressPercentage: user.status?.progress_percentage ?? 0,
        paths: user.status?.educational_paths ?? [],
        totals: {
          maintenance_minutes_required_total:
            user.status?.maintenance_minutes_required_total,
          maintenance_minutes_completed_total:
            user.status?.maintenance_minutes_completed_total,
          maintenance_minutes_intended_total:
            user.status?.maintenance_minutes_intended_total,
          registered_percentage: user.status?.registered_percentage,
          progress_percentage: user.status?.progress_percentage,
        },
        year: new Date().getFullYear(),
      }),
      sortable: true,
      alignment: 'center',
      width: '200px'
    }
  ];

  /* ---------------------------------------------------------------------- */
  /*  Dynamic columns based on user permissions                              */
  /* ---------------------------------------------------------------------- */
  let columns: any[] = [...baseColumns];
  
  // Always add educational paths column
  columns.push(educationalPathsColumn);
  
  // Check permissions and add sensitive columns + actions
  authService.hasAnyRole$(ALLOWED_ROLES)
      .pipe(take(1))
      .subscribe(hasPermissions => {
        if (hasPermissions) {
          // User has permissions - show all columns with full functionality
          columns.push(...sensitiveColumns);
          
          // Add actions column
          columns.push({
            key: 'actions',
            header: '',
            type: 'button',
            buttonText: 'Profil',
            buttonColor: 'primary',
            alignment: 'right',
            variant: 'raised',
            action: (item: User) => router.navigate(['/profile', item.uuid])
          });
          
          // Update educational paths to show status
          const educationalPathsIndex = columns.findIndex(col => col.key === 'educational_paths');
          if (educationalPathsIndex !== -1) {
            columns[educationalPathsIndex] = {
              ...columns[educationalPathsIndex],
              componentInputs: (user: any) => {
                const educationalPaths = user.status?.educational_paths || [];
                
                return {
                  educationalPaths: educationalPaths,
                  currentYear: new Date().getFullYear(),
                  user: user,
                  showStatus: true // Show status for users with permissions
                };
              }
            };
          }
        } else {
          // User doesn't have permissions - educational paths without status
          const educationalPathsIndex = columns.findIndex(col => col.key === 'educational_paths');
          if (educationalPathsIndex !== -1) {
            columns[educationalPathsIndex] = {
              ...columns[educationalPathsIndex],
              componentInputs: (user: any) => {
                const educationalPaths = user.status?.educational_paths || [];
                
                return {
                  educationalPaths: educationalPaths,
                  currentYear: new Date().getFullYear(),
                  user: user,
                  showStatus: false // Hide status for users without permissions
                };
              }
            };
          }
        }
      })

  /* ---------------------------------------------------------------------- */
  /*  Return the data-source object                                         */
  /* ---------------------------------------------------------------------- */
  return {
    name: 'Location Users',
    description: 'List of users in the current location',
    execute: (params?: LoadDataParams) => {
      /* ---------- location GUID from filters or URL ---------------------- */
      const guidFromParams = params?.filters?.find(
        (f) => f.column === 'location_guid'
      )?.value;
      let guid =
        typeof guidFromParams === 'string' && guidFromParams.trim()
          ? guidFromParams.trim()
          : null;
      if (!guid) {
        const seg = router.url.split('/');
        const idx = seg.indexOf('lokation');
        if (idx !== -1 && idx < seg.length - 1) {
          guid = seg[idx + 1];
        }
      }
      if (!guid) {
        console.error('[locations_users] No location GUID in params or URL');
        return of({ items: [], totalCount: 0 });
      }

      // Ensure sort is always set so the API returns sorted data (table sends sort on column click; apply default when missing)
      const settings = componentDataSourceService.getDataSourceSettings('locations_users');
      const effectiveSort = params?.sort?.active
        ? params.sort
        : {
            active: settings?.defaultSortColumn || 'name',
            direction: (settings?.defaultSortDirection === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc'
          };
      const effectiveParams: LoadDataParams = {
        ...params,
        dataSourceKey: 'locations_users',
        sort: effectiveSort
      };

      /* ---------- Use users/with-progress API with location GUID filter --- */
      // Add location GUID to the filters if params exist, otherwise create new params
      const existingFilters = (effectiveParams?.filters || []).filter(
        (f) => f.column !== 'location_guid'
      );
      const locationParams: LoadDataParams = {
        ...effectiveParams,
        filters: [
          ...existingFilters,
          {
            column: 'location_guid',
            value: guid,
            operator: 'equals',
          },
        ],
      };

      // Use the locations/users endpoint instead of users/with-progress
      return componentDataSourceService['loadDataWithStandardFiltersGET']('locations_users', '/locations/users', locationParams).pipe(
        catchError(error => {
          console.error('[locations_users] Error fetching location users:', error);
          return of({ items: [], totalCount: 0 });
        }),
        map(result => {
          let users: User[] = [];

          if (result?.items) {
            try {
              const uniqueUserDTOs = result.items.reduce((acc: UserDTO[], userDto: UserDTO) => {
                if (!acc.some(existing => existing.uuid === userDto.uuid)) {
                  acc.push(userDto);
                }
                return acc;
              }, []);

              users = uniqueUserDTOs.map((userDto: UserDTO) => {
                const user = User.fromDTO(userDto);
                if (userDto.groups) {
                  user.groups = userDto.groups;
                }
                if (user.status?.validation_status) {
                }
                return user;
              });
            } catch (error) {
              console.error('[locations_users] Error processing users:', error);
              return { items: [], totalCount: 0, settings: buildTableSettings() };
            }
          } else {
          }

          if (result?.meta || users.length > 0) {
            syncGroupUi(result?.meta ?? {}, users);
          }

          return {
            items: users,
            totalCount: result?.totalCount ?? 0,
            pageSize: result?.pageSize,
            pageIndex: result?.pageIndex,
            filters: result?.filters,
            meta: result?.meta,
            settings: buildTableSettings(),
          };
        })
      );
    },
    settings: buildTableSettings(),
  };
}

/* -------------------------------------------------------------------------- */
/*  Helper                                                                     */
/* -------------------------------------------------------------------------- */
function translateRole(role: string): string {
  switch (role.toLowerCase()) {
    case 'primaer':       return 'Primær';
    case 'sekundaer':     return 'Sekundær';
    case 'uddansvarlig':  return 'Udd. Ansvarlig';
    case 'uddassistent':  return 'Udd. Assistent';
    case 'oevrig':        return 'Øvrig';
    case 'superadmin':    return 'Superadmin';
    case 'orgadmin':      return 'Orgadmin';
    default:              return role;
  }
}

function getValidationTooltip(status: string): string {
  const lastYear = new Date().getFullYear() - 1;
  
  switch (status) {
    case 'valid':
      return `DUNA krav opfyldt for ${lastYear}`;
    case 'attention':
      return `DUNA kræver opmærksomhed - tjek venligst deltagelse og moduler for ${lastYear}`;
    case 'no_data':
      return `Ingen DUNA data tilgængelig for ${lastYear}`;
    case 'no_paths':
      return 'Ingen uddannelsesveje tildelt';
    default:
      return 'Ukendt DUNA status';
  }
}
